import React, { Component } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { Arwes, Button, Frame as FrameC, Words } from "arwes"
import styled from "styled-components"
import Icon from "@mdi/react"
import { mdiChemicalWeapon, mdiCancel } from "@mdi/js"
import { Field, getFormValues, reduxForm } from "redux-form"

import { MarginTop } from "../../common/styled/MarginTop"
import {
  ContentColumn,
  PageBlock as SharedPageBlock,
  PageOffset
} from "../../common/styled/Page"
import { createCell } from "./redux/reducers"

const PageBlock = styled(SharedPageBlock)`
  margin: 30px;

  @media (max-width: 900px) {
    margin: 16px 12px;
  }

  @media (max-width: 360px) {
    margin-left: 8px;
    margin-right: 8px;
  }
`

const Field2 = styled.div`
  padding: 14px 0;

  @media (max-width: 600px) {
    padding: 10px 0;
  }
`

const Label = styled.div`
  font-family: "Rajdhani", "VT323", monospace;
  font-size: 0.85rem;
  color: #fc26fa;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 6px;
`

const VisibilityRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`

const VisibilityOption = styled.label`
  flex: 1;
  border: 1px solid ${props => (props.checked ? "#17ff0e" : "#3a4670")};
  background: ${props => (props.checked ? "rgba(23,255,14,0.06)" : "transparent")};
  padding: 10px 14px;
  cursor: pointer;
  color: ${props => (props.checked ? "#17ff0e" : "#6fbfff")};
  font-family: "Rajdhani", "VT323", monospace;
  display: block;

  input {
    display: none;
  }

  .opt-title {
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.1em;
  }
  .opt-sub {
    font-family: "VT323", monospace;
    font-size: 0.75rem;
    margin-top: 4px;
    color: #6fbfff;
  }
`

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 18px;
`

const Hint = styled.div`
  font-family: "VT323", monospace;
  font-size: 0.8rem;
  color: #6fbfff;
  margin-top: 4px;
`

const ErrorBox = styled.div`
  box-sizing: border-box;
  margin: 0 30px 18px;
  padding: 10px 14px;
  border: 1px solid #ff001c;
  color: #ff001c;
  font-family: "VT323", monospace;

  @media (max-width: 900px) {
    margin: 0 12px 18px;
  }

  @media (max-width: 360px) {
    margin-left: 8px;
    margin-right: 8px;
  }
`

const Form = styled.form`
  padding: 24px;

  .form-control {
    box-sizing: border-box;
    max-width: 100%;
  }

  @media (max-width: 600px) {
    padding: 16px 14px;
  }
`

const renderInput = ({ input, type, placeholder, rows, meta }) => {
  if (rows) {
    return (
      <textarea
        className="form-control full-width"
        rows={rows}
        placeholder={placeholder}
        {...input}
      />
    )
  }
  return (
    <input
      type={type || "text"}
      className="form-control full-width"
      placeholder={placeholder}
      {...input}
    />
  )
}

const renderVisibility = ({ input }) => {
  const value = input.value || "public"
  return (
    <VisibilityRow>
      <VisibilityOption checked={value === "public"}>
        <input
          type="radio"
          name={input.name}
          value="public"
          checked={value === "public"}
          onChange={() => input.onChange("public")}
        />
        <div className="opt-title">● PUBLIC</div>
        <div className="opt-sub">anyone can find &amp; join</div>
      </VisibilityOption>
      <VisibilityOption checked={value === "private"}>
        <input
          type="radio"
          name={input.name}
          value="private"
          checked={value === "private"}
          onChange={() => input.onChange("private")}
        />
        <div className="opt-title">○ PRIVATE</div>
        <div className="opt-sub">invite only</div>
      </VisibilityOption>
    </VisibilityRow>
  )
}

class CreateCell extends Component {
  state = { show: false }

  componentDidMount() {
    setTimeout(() => this.setState({ show: true }), 400)
  }

  onSubmit = values => {
    const payload = {
      name: (values.name || "").trim().toLowerCase(),
      description: (values.description || "").trim(),
      visibility: values.visibility === "private" ? "private" : "public"
    }
    this.props.createCell(payload)
  }

  render() {
    const { handleSubmit, isExpanded, isError, errorMessage } = this.props
    return (
      <Arwes>
        <PageOffset isExpanded={isExpanded}>
          <MarginTop />
          <ContentColumn maxWidth={720}>
            <PageBlock>
              <FrameC
                show={this.state.show}
                animate
                level={2}
                corners={4}
                layer="alert"
              >
                <h3 style={{ textAlign: "center", margin: "10px 0" }}>
                  <Words animate layer="alert">
                    Create Cell
                  </Words>
                </h3>
              </FrameC>
            </PageBlock>

            {isError && errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}

            <PageBlock>
              <FrameC
                show={this.state.show}
                animate
                level={3}
                corners={3}
                layer="primary"
              >
                <Form onSubmit={handleSubmit(this.onSubmit)}>
                  <Field2>
                    <Label>◇ Name</Label>
                    <Field
                      name="name"
                      component={renderInput}
                      placeholder="mesh-ops"
                    />
                    <Hint>lowercase, hyphens · 3-24 chars</Hint>
                  </Field2>

                  <Field2>
                    <Label>◇ Description</Label>
                    <Field
                      name="description"
                      component={renderInput}
                      rows={3}
                      placeholder="One line. What's this cell about?"
                    />
                  </Field2>

                  <Field2>
                    <Label>◇ Visibility</Label>
                    <Field name="visibility" component={renderVisibility} />
                  </Field2>

                  <Actions>
                    <Link to="/cells" style={{ textDecoration: "none" }}>
                      <Button animate layer="alert" buttonProps={{ type: "button" }}>
                        <Icon path={mdiCancel} size={0.6} color="#ff001c" /> Cancel
                      </Button>
                    </Link>
                    <Button animate layer="primary">
                      <Icon
                        path={mdiChemicalWeapon}
                        size={0.6}
                        color="#be26fc"
                        spin
                      />{" "}
                      Create Cell
                    </Button>
                  </Actions>
                </Form>
              </FrameC>
            </PageBlock>

            <PageBlock>
              <Hint>
                {"// v1 scope · name, description, visibility · members can be "}
                invited from the cell page after creation
              </Hint>
            </PageBlock>
          </ContentColumn>
        </PageOffset>
      </Arwes>
    )
  }
}

const FORM_NAME = "CreateCell"
const form = reduxForm({
  form: FORM_NAME,
  initialValues: { visibility: "public" }
})(CreateCell)

const mapState = state => ({
  formValues: getFormValues(FORM_NAME)(state),
  isError: state.cells.isError,
  errorMessage: state.cells.errorMessage
})

export default connect(
  mapState,
  { createCell }
)(form)
